import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Agent } from '../lib/api';
import type { CognitoSessionInfo } from '../lib/cognito';

// Phase 3 auth — wraps Cognito session.
// Admin signs in via Cognito → stores session + role=admin.
// Agents (later) sign in the same way → role=agent.
// Optional admin impersonation for QA-ing the agent portal view.

interface User {
  id: string; // sub from Cognito (or 'admin' for stub)
  display_name: string;
  email: string;
  role: 'admin' | 'agent';
}

interface AuthState {
  user: User | null; // effective user — null when not signed in
  realUser: User | null; // actual signed-in user (always admin in current scope)
  cognitoSession: CognitoSessionInfo | null;
  impersonating: Agent | null;
  setCognitoSession: (session: CognitoSessionInfo | null) => void;
  startImpersonation: (agent: Agent) => void;
  stopImpersonation: () => void;
  signOut: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      realUser: null,
      cognitoSession: null,
      impersonating: null,
      setCognitoSession: (session) => {
        if (!session) {
          set({ user: null, realUser: null, cognitoSession: null, impersonating: null });
          return;
        }
        const u: User = {
          id: session.sub,
          display_name: session.email,
          email: session.email,
          // Phase 3 simple model: known admin emails are admin; anyone else
          // is agent. Default admin is admin@woxomhealth.com; extra admin
          // emails (e.g. for review/testing accounts) come from
          // VITE_ADMIN_EMAILS (CSV). Phase 4 will store role via a custom
          // Cognito attribute or /me lookup.
          role: ((): 'admin' | 'agent' => {
            const extra = (import.meta.env.VITE_ADMIN_EMAILS as string | undefined) || '';
            const set = new Set(
              ['admin@woxomhealth.com', ...extra.split(',')]
                .map((s) => s.trim().toLowerCase())
                .filter(Boolean),
            );
            return set.has(String(session.email || '').toLowerCase()) ? 'admin' : 'agent';
          })(),
        };
        set({
          user: u,
          realUser: u,
          cognitoSession: session,
          impersonating: null,
        });
      },
      startImpersonation: (agent) => {
        const { realUser } = get();
        if (!realUser || realUser.role !== 'admin') return;
        set({
          impersonating: agent,
          user: {
            id: agent.agent_id,
            display_name: agent.display_name,
            email: agent.email ?? '',
            role: 'agent',
          },
        });
      },
      stopImpersonation: () => {
        const { realUser } = get();
        set({ impersonating: null, user: realUser });
      },
      signOut: () => {
        // Cognito sign-out is performed by the caller via cognito.signOut() —
        // we just clear our store here.
        set({ user: null, realUser: null, cognitoSession: null, impersonating: null });
      },
    }),
    { name: 'eap-auth' },
  ),
);

export const useIsAdmin = () => useAuthStore((s) => s.user?.role === 'admin');
export const useIsAgent = () => useAuthStore((s) => s.user?.role === 'agent');
export const useIsImpersonating = () => useAuthStore((s) => s.impersonating !== null);
export const useIsSignedIn = () => useAuthStore((s) => s.user !== null);
